import { Spinner } from '@/ui-kit'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui-kit/components/TabsShadcn'
import { SCREEN_SIZE, useMediaQuery } from '@/ui-kit/media'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MISSION_AVAILABILITY_REASON_UNAVAILABLE, type Mission } from './api/models'
import { MissionCard } from './components/MissionCard'
import {
  MissionsFiltersEmptyState,
  MissionsListEmptyState,
  MissionsTabEmptyState
} from './components/MissionEmptyState'
import {
  MISSION_FILTERS_DEFAULT,
  MissionFilters,
  type MissionFiltersValue,
  applyMissionFilters
} from './components/MissionFilters'

type MissionTab = 'QUALIFY' | 'VALIDATING' | 'COMPLETED' | 'UNAVAILABLE'

const MISSION_TAB_ORDER: MissionTab[] = ['QUALIFY', 'VALIDATING', 'COMPLETED', 'UNAVAILABLE']

const MISSION_TAB_LABEL: Record<MissionTab, MessageDescriptor> = {
  QUALIFY: msg`Available`,
  VALIDATING: msg`In review`,
  COMPLETED: msg`Completed`,
  UNAVAILABLE: msg`Unavailable`
}

const getMissionTab = (mission: Mission): MissionTab => {
  if (
    mission.status === 'ENDED' ||
    (!mission.availability?.available && MISSION_AVAILABILITY_REASON_UNAVAILABLE.includes(mission.availability?.reason))
  )
    return 'UNAVAILABLE'
  if (!mission.view) return 'QUALIFY'

  const { latestSubmissionStatus, nextSubmissionAvailable } = mission.view
  if (latestSubmissionStatus === 'SUBMITTED') return 'VALIDATING'
  if ((latestSubmissionStatus === 'COMPLETED' || latestSubmissionStatus === 'FAILED') && !nextSubmissionAvailable) {
    return 'COMPLETED'
  }
  return 'QUALIFY'
}

function groupMissionsByTab(missions: Mission[]) {
  const result: Record<MissionTab, Mission[]> = { QUALIFY: [], VALIDATING: [], COMPLETED: [], UNAVAILABLE: [] }
  for (const mission of missions) {
    result[getMissionTab(mission)].push(mission)
  }
  return result
}

const CARD_WIDTH_MOBILE = 320
const CARD_WIDTH = 382

export type MissionsProps = {
  availableMissions: Mission[]
  isInitialLoad?: boolean
  isAuthorized?: boolean
}

export const Missions = ({ availableMissions, isInitialLoad = false, isAuthorized = false }: MissionsProps) => {
  const { t } = useLingui()
  const [searchParams] = useSearchParams()
  const missionIdParam = searchParams.get('missionId')
  const [highlightMissionId, setHighlightMissionId] = useState<string | null>(null)
  const [filters, setFilters] = useState<MissionFiltersValue>(MISSION_FILTERS_DEFAULT)
  const [activeTab, setActiveTab] = useState<MissionTab>('QUALIFY')

  const isMobile = useMediaQuery(SCREEN_SIZE.MOBILE)

  const filteredMissions = useMemo(() => applyMissionFilters(availableMissions, filters), [availableMissions, filters])

  const hasActiveFilters = filters.search.trim().length > 0 || filters.group !== null

  const missionsByTab = useMemo(() => groupMissionsByTab(filteredMissions), [filteredMissions])

  const visibleTabs = useMemo(
    () => MISSION_TAB_ORDER.filter((tab) => tab !== 'UNAVAILABLE' || missionsByTab.UNAVAILABLE.length > 0),
    [missionsByTab]
  )

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? 'QUALIFY')
    }
  }, [visibleTabs, activeTab])

  useEffect(() => {
    if (!missionIdParam) {
      setHighlightMissionId(null)
      return
    }
    if (isInitialLoad) return
    if (!availableMissions.some((m) => m.id === missionIdParam)) return

    setHighlightMissionId(missionIdParam)
    const clearHighlight = window.setTimeout(() => setHighlightMissionId(null), 3000)
    const scrollToCard = () => {
      document.getElementById(`mission-card-${missionIdParam}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
    requestAnimationFrame(() => requestAnimationFrame(scrollToCard))

    return () => clearTimeout(clearHighlight)
  }, [isInitialLoad, missionIdParam, availableMissions])

  const gridStyle = {
    gridTemplateColumns: `repeat(auto-fill,${isMobile ? `max(${CARD_WIDTH_MOBILE}px, calc(100vw - 40px))` : `${CARD_WIDTH}px`})`
  }

  return (
    <div className="max-mobile:gap-6 flex flex-col gap-10">
      {isInitialLoad && (
        <div className="flex w-full justify-center">
          <Spinner className="mt-8" />
        </div>
      )}
      {!isInitialLoad && !!availableMissions.length && (
        <MissionFilters missions={availableMissions} value={filters} onChange={setFilters} />
      )}
      {!isInitialLoad && (
        <>
          {!availableMissions.length && <MissionsListEmptyState />}
          {!!availableMissions.length && !filteredMissions.length && hasActiveFilters && <MissionsFiltersEmptyState />}
          {!!availableMissions.length && !!filteredMissions.length && isAuthorized && (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as MissionTab)}>
              <div className="scrollbar-hidden -mx-4 max-w-[calc(100%+32px)] overflow-x-auto px-4">
                <TabsList className="w-max max-w-none">
                  {visibleTabs.map((tab) => (
                    <TabsTrigger key={tab} value={tab} className="flex-none">
                      {t(MISSION_TAB_LABEL[tab])} ({missionsByTab[tab].length})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {visibleTabs.map((tab) => (
                <TabsContent key={tab} value={tab}>
                  {missionsByTab[tab].length === 0 ? (
                    <MissionsTabEmptyState label={t(MISSION_TAB_LABEL[tab])} />
                  ) : (
                    <ul className="grid gap-4" style={gridStyle}>
                      {missionsByTab[tab].map((mission) => (
                        <MissionCard
                          key={mission.id}
                          mission={mission}
                          highlighted={highlightMissionId === mission.id}
                        />
                      ))}
                    </ul>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
          {!!availableMissions.length && !!filteredMissions.length && !isAuthorized && (
            <ul className="grid gap-4" style={gridStyle}>
              {filteredMissions.map((mission) => (
                <MissionCard key={mission.id} mission={mission} highlighted={highlightMissionId === mission.id} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
